/* ── googleMapsLoader ────────────────────────────────────────
 * Singleton loader for the Google Maps JavaScript API.
 *
 * Deliberately dependency-free: the only thing a React wrapper
 * would buy us is marker lifecycle, and MapPicker's pin is a CSS
 * overlay rather than a google.maps.Marker — so there is nothing
 * left to wrap.
 *
 * Three properties that matter:
 *
 *   1. The <script> is injected exactly once per page, no matter
 *      how many MapPicker instances mount or how many times React
 *      18 StrictMode double-invokes an effect. Everything funnels
 *      through one module-level promise.
 *   2. Library handles are cached per name, so `places` resolves
 *      instantly for the second consumer.
 *   3. Failures are *typed and surfaced*, never swallowed. The
 *      previous Leaflet/Nominatim picker silently cleared its
 *      suggestions on error, which made a rate-limited geocoder
 *      indistinguishable from "no results" — the single most
 *      confusing failure mode it had.
 * ──────────────────────────────────────────────────────────── */

export const MapsErrorCode = {
  MISSING_KEY: 'MISSING_KEY',
  LOAD_FAILED: 'LOAD_FAILED',
  AUTH_FAILED: 'AUTH_FAILED',
};

export class MapsLoadError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'MapsLoadError';
    this.code = code;
  }
}

const SCRIPT_ID = 'wa-google-maps-js';
const CALLBACK_NAME = '__waGoogleMapsReady__';

let bootstrapPromise = null;
const libraryPromises = new Map();

/** Auth failures surface asynchronously, long after the script "loads". */
let authFailed = false;
const authListeners = new Set();

export const getApiKey = () =>
  (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

export const hasApiKey = () => getApiKey().length > 0;

/**
 * Google calls window.gm_authFailure when the key is invalid, has the
 * wrong referrer restriction, or has billing disabled. The script tag
 * itself resolves fine in that case — the map just renders a grey
 * "For development purposes only" slab. Without this hook there is no
 * way to tell a broken key from a working one, so we install it before
 * the script is ever appended.
 */
const installAuthFailureHook = () => {
  if (typeof window === 'undefined') return;
  const previous = window.gm_authFailure;
  window.gm_authFailure = () => {
    authFailed = true;
    authListeners.forEach((fn) => {
      try {
        fn();
      } catch {
        /* a listener throwing must not stop the others */
      }
    });
    if (typeof previous === 'function') previous();
  };
};

export const onAuthFailure = (listener) => {
  if (authFailed) listener();
  authListeners.add(listener);
  return () => authListeners.delete(listener);
};

export const isAuthFailed = () => authFailed;

const bootstrap = () => {
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      reject(new MapsLoadError(MapsErrorCode.LOAD_FAILED, 'No DOM available.'));
      return;
    }

    // Another bundle (or a hot reload) may already have it.
    if (window.google?.maps?.importLibrary) {
      resolve(window.google.maps);
      return;
    }

    const key = getApiKey();
    if (!key) {
      reject(
        new MapsLoadError(
          MapsErrorCode.MISSING_KEY,
          'REACT_APP_GOOGLE_MAPS_API_KEY is not set.'
        )
      );
      return;
    }

    installAuthFailureHook();

    window[CALLBACK_NAME] = () => {
      delete window[CALLBACK_NAME];
      resolve(window.google.maps);
    };

    const params = new URLSearchParams({
      key,
      // `weekly` is what exposes the Places API (New) classes
      // (AutocompleteSuggestion / Place). The frozen quarterly
      // channels still ship the retired AutocompleteService.
      v: 'weekly',
      loading: 'async',
      callback: CALLBACK_NAME,
    });

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
    script.async = true;
    script.defer = true;
    // Respect a CSP nonce if the host page uses one.
    const nonce = document.querySelector('script[nonce]')?.nonce;
    if (nonce) script.nonce = nonce;

    script.onerror = () => {
      // Let a later mount retry rather than caching the rejection forever
      // (this is usually transient offline / blocked-by-extension).
      bootstrapPromise = null;
      delete window[CALLBACK_NAME];
      script.remove();
      reject(
        new MapsLoadError(
          MapsErrorCode.LOAD_FAILED,
          'The Google Maps JavaScript API could not be loaded.'
        )
      );
    };

    document.head.appendChild(script);
  });

  return bootstrapPromise;
};

/**
 * Resolve a Maps library by name ('maps' | 'places' | 'geocoding' | 'core').
 * Cached, so concurrent callers share one in-flight request.
 */
export const loadMapsLibrary = async (name) => {
  if (libraryPromises.has(name)) return libraryPromises.get(name);

  const promise = bootstrap()
    .then((maps) => maps.importLibrary(name))
    .catch((err) => {
      libraryPromises.delete(name);
      throw err;
    });

  libraryPromises.set(name, promise);
  return promise;
};

export default loadMapsLibrary;
